import classNames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { marketing } from 'config/global';
import imgPhone from 'assets/images/v3-hero-phone.png';
import { PlatformRow } from './components/PlatformRow';
import { StoreBadge } from './components/StoreBadge';

// Apple logo — reused for the platform icon and the App Store badge.
const AppleGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path d="M16.5 1.6c.1 1-.3 2-.9 2.8-.7.8-1.7 1.4-2.7 1.3-.1-1 .4-2 1-2.7.6-.8 1.7-1.3 2.6-1.4zM19.6 17c-.5 1.2-.8 1.7-1.4 2.7-.9 1.4-2.2 3.2-3.8 3.2-1.4 0-1.8-.9-3.7-.9s-2.4.9-3.7.9c-1.6 0-2.8-1.6-3.7-3C.8 16.6.5 11.4 2.6 8.6c1-1.4 2.5-2.2 4-2.2 1.5 0 2.5 1 3.8 1 1.2 0 2-1 3.8-1 1.3 0 2.7.7 3.7 2-3.3 1.8-2.7 6.4 1.7 8.6z" />
  </svg>
);

const PhoneStage = () => {
  const t = useTranslations();
  return (
    <div className={styles.stage}>
      <Image
        src={imgPhone}
        alt={t('pages.download.ios.imageAlt')}
        width={440}
        height={916}
        className={styles.phone}
        sizes="262px"
      />
      <div className={styles.badge}>
        <span className={styles.badgeIcon}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
        {t('pages.download.ios.badge')}
      </div>
    </div>
  );
};

// Download page — MindFeed for iOS
export const Section38 = () => {
  const t = useTranslations();
  return (
    <PlatformRow
      id="ios"
      label={t('pages.download.ios.label')}
      icon={<AppleGlyph className="gly" />}
      tag={t('pages.download.ios.tag')}
      title={t('pages.download.ios.title')}
      description={t('pages.download.ios.description')}
      features={[
        t('pages.download.ios.feature1'),
        t('pages.download.ios.feature2'),
        t('pages.download.ios.feature3'),
      ]}
      actions={
        <StoreBadge
          icon={<AppleGlyph />}
          small={t('pages.download.ios.storeSmall')}
          big="App Store"
          href={marketing.appStoreUri}
          newTab
        />
      }
      visual={<PhoneStage />}
    />
  );
};

const styles = {
  stage: 'relative z-[2] flex items-center justify-center',
  phone: [
    'h-auto w-[236px] rounded-[42px] lg:w-[262px]',
    'drop-shadow-[0_18px_36px_rgba(20,30,60,0.14)]',
    'marketing-dark:drop-shadow-[0_40px_70px_rgba(2,8,30,0.6)]',
  ].join(' '),
  badge: classNames(
    'absolute -right-20 bottom-5 z-[6] hidden items-center gap-[9px] whitespace-nowrap rounded-xl lg:flex',
    'border px-[13px] py-[9px] text-[12.5px] font-semibold',
    'border-[#DEDEDE] bg-white text-mf-off-black-600 shadow-[0_16px_36px_rgba(20,30,60,0.16)]',
    'marketing-dark:border-white/[0.14] marketing-dark:bg-[#0b1430] marketing-dark:text-white',
    'marketing-dark:shadow-[0_16px_36px_rgba(0,0,0,0.45)] animate-floaty motion-reduce:animate-none',
  ),
  badgeIcon:
    'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[7px] bg-[#1f7a52] [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:fill-none [&>svg]:stroke-white [&>svg]:stroke-2',
};
